import { size } from "lodash";
import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
} from "react";

import { GalleryTabData, GalleryTabOptions } from "@/data/types";

interface GalleryTabContextType {
  options: GalleryTabOptions;
  data: GalleryTabData[];
  canMoveUp(itemIndex: number): boolean;
  canMoveDown(itemIndex: number): boolean;
}

const GalleryTabContext = createContext<GalleryTabContextType | null>(null);

type Props = PropsWithChildren<{
  options: GalleryTabOptions;
  data: GalleryTabData[];
}>;

export const GalleryTabProvider: FC<Props> = ({ children, options, data }) => {
  const canMoveUp = useCallback((itemIndex: number) => {
    const isFirstItem = itemIndex === 0;

    return !isFirstItem;
  }, []);

  const canMoveDown = useCallback(
    (itemIndex: number) => {
      const isLastItem = itemIndex === size(data) - 1;

      return !isLastItem;
    },
    [data],
  );

  return (
    <GalleryTabContext.Provider
      value={{ options, data, canMoveUp, canMoveDown }}
    >
      {children}
    </GalleryTabContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useGalleryTabContext = () => {
  const context = useContext(GalleryTabContext);

  if (!context) {
    throw new Error(
      "useGalleryTabContext must be used within an GalleryTabProvider",
    );
  }

  return context;
};
